import { Text } from '@loomhq/lens';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CEE, ENTER, ESCAPE } from 'constants/keyCodes';
import { isMobile } from 'utilities/device';
import { secondsToVideoTS } from 'utilities/time';

import {
  selectShareVideoScopes,
  selectUserCanEditVideo,
  selectVideoDescription,
  selectVideoDuration,
  selectVideoId,
} from 'selectors/shareVideo';
import { selectPlayerTime } from 'selectors/videoPlayer';

import Description, { isEmpty } from './linked-description';
import { updateVideoDescription } from './graphql';

import AutoresizeTextarea from 'components/ui-components/autoresize-textarea';

import styles from './styles.module.less';

const ADD_DESCRIPTION_MESSAGE = 'Add a description...';

const protect = description => description || '';
const isFirstUpdate = storedDescription => storedDescription == null;

const VideoDescription = () => {
  const dispatch = useDispatch();

  const videoId = useSelector(selectVideoId);
  const videoDuration = useSelector(selectVideoDuration);
  const storedDescription = useSelector(selectVideoDescription);

  const userCanEditVideo = useSelector(selectUserCanEditVideo);
  const currentTime = useSelector(selectPlayerTime);
  const scopes = useSelector(selectShareVideoScopes);

  const ref = useRef(null);
  const [showInput, setShowInput] = useState(false);

  const [description, setDescription] = useState(protect(storedDescription));

  const onSaveDescription = useCallback(
    newDescription => {
      setDescription(newDescription.trim());
      dispatch(
        updateVideoDescription(
          dispatch,
          videoId,
          newDescription.trim(),
          videoDuration,
          isFirstUpdate(storedDescription)
        )
      );
      setShowInput(false);
    },
    [dispatch, videoId, videoDuration, storedDescription]
  );

  const setShowInputToTrue = useCallback(() => setShowInput(true), []);

  const saveIfEnterOrEscapePressed = e => {
    if (e.keyCode === CEE && e.ctrlKey && e.shiftKey) {
      const hasNoDescription = isEmpty(description);
      const chapter = `${hasNoDescription ? '' : '\n'}${secondsToVideoTS(
        Math.round(currentTime)
      )} - `;

      setDescription(description + chapter);
    }

    if (e.keyCode === ESCAPE) {
      setDescription(protect(storedDescription));
      setShowInput(false);
    } else if (e.keyCode === ENTER && !e.shiftKey) {
      onSaveDescription(description);
    }
  };

  useEffect(() => {
    if (ref && ref.current && showInput) {
      const ele = ref.current;

      // focus the text area
      ele.focus();
      const end = ele.value.length;

      // place cursor at the end
      ele.setSelectionRange(end, end);
    }
  }, [showInput]);

  const canEdit =
    userCanEditVideo && !isMobile && scopes.hasVideoToolsAccessScope;

  return (
    <>
      {showInput && canEdit ? (
        <>
          <AutoresizeTextarea
            textRef={ref}
            className={cx(
              styles.autoHeight,
              styles.resizeVert,
              styles.input,
              styles.hintPadding
            )}
            value={description}
            placeholder={ADD_DESCRIPTION_MESSAGE}
            onChange={e => setDescription(e.target.value)}
            onKeyDown={saveIfEnterOrEscapePressed}
            onBlur={() => onSaveDescription(description)}
          />
          {description?.length > 0 ? (
            <div className={styles.saveHint}>
              <Text className={styles.bold}>Shift-enter</Text>
              <Text>for a new line</Text>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {(description || canEdit) && (
            <Description
              canEdit={canEdit}
              onClick={setShowInputToTrue}
              defaultText={ADD_DESCRIPTION_MESSAGE}
              description={description}
            />
          )}
        </>
      )}
    </>
  );
};

export default VideoDescription;
